import { useTranslation } from "@next-platform/client-common-core";
import type { LorebookVisibilitySource } from "@next-platform/client-common-core/lorebook/useLorebookVisibilityConfirmation";
import { Button, ButtonSize, ButtonVariant, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { LorebookVisibility } from "@next-platform/packages-ts-types";
import type { ReactNode } from "react";

import Trans from "@/components/Trans/Trans";
import { FEATURE_FLAGS } from "@/constants";
import { useFeatureFlag } from "@/hooks/useFeatureFlag";

import LorebookMakePrivateModal from "./LorebookMakePrivateModal";
import useLorebookVisibilityField from "./useLorebookVisibilityField";

type LorebookVisibilityFieldProps = {
  lorebook?: LorebookVisibilitySource;
  value: LorebookVisibility;
  onChange: (visibility: LorebookVisibility) => void;
  labelId: string;
  inputId: string;
  helperId: string;
  label: ReactNode;
  helperText?: string;
  disabled: boolean;
  "data-testid"?: string;
};

const LorebookVisibilityField = ({
  lorebook,
  value,
  onChange,
  labelId,
  inputId,
  helperId,
  label,
  helperText,
  disabled,
  "data-testid": testId = "LorebookVisibilityField",
}: LorebookVisibilityFieldProps) => {
  const { hasFeatureFlag: hasLorebookPublic } = useFeatureFlag(FEATURE_FLAGS.LOREBOOK_PUBLIC);
  const { t } = useTranslation();
  const { isConfirmationOpen, onConfirm, onDismiss, onPublicClick, onPrivateClick } = useLorebookVisibilityField({
    lorebook,
    value,
    onChange,
    disabled,
  });
  const isPermanentlyPrivate = value === LorebookVisibility.PERMANENTLY_PRIVATE;
  const isPrivate = value === LorebookVisibility.PRIVATE || isPermanentlyPrivate;
  let description = helperText;
  if (value === LorebookVisibility.PUBLIC) description = t("lorebook:form.field.visibility.helper.public");
  if (isPrivate) description = t("lorebook:form.field.visibility.helper.private");
  if (isPermanentlyPrivate) description = t("lorebook:form.field.visibility.helper.permanentlyPrivate");

  return (
    <div className="flex flex-col gap-sm" data-field-name="visibility" data-testid={testId}>
      <Text id={labelId} variant={TextVariants.LABEL_LARGE_MEDIUM} as="label" htmlFor={inputId}>
        {label}
      </Text>
      <div id={inputId} className="flex flex-row gap-sm" role="group" aria-labelledby={labelId} aria-describedby={helperId}>
        <Button
          size={ButtonSize.MEDIUM}
          variant={value === LorebookVisibility.PUBLIC ? ButtonVariant.INVERSE : ButtonVariant.OUTLINED}
          onClick={onPublicClick}
          disabled={!hasLorebookPublic || disabled}
          aria-pressed={value === LorebookVisibility.PUBLIC}
          data-testid="LorebookVisibilityField-PublicButton"
        >
          <Trans i18nKey="lorebook:form.field.visibility.public" />
        </Button>
        <Button
          size={ButtonSize.MEDIUM}
          variant={isPrivate ? ButtonVariant.INVERSE : ButtonVariant.OUTLINED}
          onClick={onPrivateClick}
          disabled={disabled}
          aria-pressed={isPrivate}
          data-testid="LorebookVisibilityField-PrivateButton"
        >
          <Trans i18nKey="lorebook:form.field.visibility.private" />
        </Button>
        <Button
          size={ButtonSize.MEDIUM}
          variant={value === LorebookVisibility.HIDDEN ? ButtonVariant.INVERSE : ButtonVariant.OUTLINED}
          disabled
          aria-pressed={value === LorebookVisibility.HIDDEN}
          data-testid="LorebookVisibilityField-UnlistedButton"
        >
          <Trans i18nKey="lorebook:form.field.visibility.unlisted" />
        </Button>
      </div>
      {description ? (
        <Text id={helperId} variant={TextVariants.LABEL_SMALL_REGULAR} className={isPermanentlyPrivate ? "text-yellow-9" : "text-gray-11"}>
          {description}
        </Text>
      ) : null}
      <LorebookMakePrivateModal isOpen={isConfirmationOpen} onConfirm={onConfirm} onDismiss={onDismiss} />
    </div>
  );
};

export default LorebookVisibilityField;
